import { useState, useEffect } from "react"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Grid,
    Snackbar,
    Alert,
    IconButton,
} from "@mui/material"
import {
    getbroadFileds,
    getNarrowFields,
    getTargetCountries,
    getIntakes
} from "../../api/PrefrenceAPI"
import { FilePenLine, Check } from "lucide-react"
import { Close } from "@mui/icons-material"

const PrefrenceModal = ({ open, onClose, onSave, editData }) => {
    const [formData, setFormData] = useState({})
    const [broadOptions, setBroadOptions] = useState([])
    const [narrowOptions, setNarrowOptions] = useState([])
    const [countryOptions, setCountryOptions] = useState([])
    const [intakePeriods, setIntakePeriods] = useState([])
    const [snackbar, setSnackbar] = useState({ open: false, message: "" })

    const educationLevels = [
        { id: 1, name: "Bachelor's" },
        { id: 2, name: "Master's" },
    ]

    const intakeYears = Array.from(
        { length: 5 },
        (_, i) => new Date().getFullYear() + i
    )

    useEffect(() => {
        if (editData) {
            setFormData({
                broad_field_id: editData.gl_broad_course_field || "",
                narrow_field_id: editData.gl_narrow_course_field || "",
                educational_level_id: editData.educational_level === "Bachelor's" ? 1 : 2,
                intake_id: editData.intake_id || "",
                intake_year: editData.intake_year || "",
                target_country_id: editData.target_country_id || "",
            })
        }
    }, [editData])

    // Fetch dropdown data
    useEffect(() => {
        const fetchDropdowns = async () => {
            try {
                const [broadRes, countryRes, intakeRes] = await Promise.all([
                    getbroadFileds(),
                    getTargetCountries(),
                    getIntakes(),
                ])
                setBroadOptions(broadRes?.data || [])
                setCountryOptions(countryRes?.data || [])
                setIntakePeriods(intakeRes?.data || [])
            } catch (err) {
                console.error("Error fetching dropdown data", err)
            }
        }
        if (open) fetchDropdowns()
    }, [open])

    // Fetch narrow fields when broad changes
    useEffect(() => {
        if (!formData.broad_field_id) return
        const fetchNarrow = async () => {
            try {
                const res = await getNarrowFields({ broad_field_id: formData.broad_field_id })
                setNarrowOptions(res?.data || [])
            } catch (err) {
                console.error("Error fetching narrow fields", err)
            }
        }
        fetchNarrow()
    }, [formData.broad_field_id])

    const handleChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
            ...(field === "broad_field_id" ? { narrow_field_id: "" } : {}),
        }))
    }

    const handleSave = async () => {
        const payload = {
            broad_field_id: formData.broad_field_id,
            narrow_field_id: formData.narrow_field_id,
            educational_status: formData.educational_level_id,
            intake_id: formData.intake_id,
            intake_year: formData.intake_year,
            target_country_id: formData.target_country_id,
        }
        onSave(payload, editData?.id)
        // try {

        //     const res = await savePrefrences(payload)
        //     onSave?.(res, editData?.id)
        // } catch (err) {
        //     console.error("Error saving preferences:", err)
        //     setSnackbar({
        //         open: true,
        //         message: err?.response?.data?.message || "Failed to save preferences. Please try again."
        //     })
        // }
    }

    const handleCloseSnackbar = () => {
        setSnackbar({ open: false, message: "" })
    }

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
                <DialogTitle className="bg-[#572575] font-siromi text-xl font-medium text-white flex justify-between items-center">
                    <span className="flex gap-2">
                        <FilePenLine className="bg-[#FFFFFF26] p-1 rounded-md" />{" "}
                        Edit Your Preference Details
                    </span>
                    <IconButton onClick={onClose} className="text-white">
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent className="p-6 ">
                    <Grid container spacing={3} className="mt-2">
                        {/* Broad Level */}
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth>
                                <InputLabel>Broad Level</InputLabel>
                                <Select
                                    value={formData.broad_field_id || ""}
                                    onChange={(e) => handleChange("broad_field_id", e.target.value)}
                                    sx={{
                                        "& .MuiInputBase-input": {
                                            color: "#9D0385",
                                            fontWeight: "bold",
                                        },
                                    }}
                                >
                                    {broadOptions.map((opt) => (
                                        <MenuItem key={opt.id} value={opt.id}>
                                            {opt.broad_field_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Narrow Level */}
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth>
                                <InputLabel>Narrow Level</InputLabel>
                                <Select
                                    value={formData.narrow_field_id || ""}
                                    onChange={(e) => handleChange("narrow_field_id", e.target.value)}
                                    disabled={!formData.broad_field_id}
                                    sx={{
                                        "& .MuiInputBase-input": {
                                            color: "#9D0385",
                                            fontWeight: "bold",
                                        },
                                    }}
                                >
                                    {narrowOptions.map((opt) => (
                                        <MenuItem key={opt.id} value={opt.id}>
                                            {opt.narrow_field_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Education Level */}
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth>
                                <InputLabel>Education Level</InputLabel>
                                <Select
                                    value={formData.educational_level_id || ""}
                                    onChange={(e) => handleChange("educational_level_id", e.target.value)}
                                    sx={{
                                        "& .MuiInputBase-input": {
                                            color: "#9D0385",
                                            fontWeight: "bold",
                                        },
                                    }}
                                >
                                    {educationLevels.map((level) => (
                                        <MenuItem key={level.id} value={level.id}>
                                            {level.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Intake Period */}
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth>
                                <InputLabel>Intake Period</InputLabel>
                                <Select
                                    value={formData.intake_id || ""}
                                    onChange={(e) => handleChange("intake_id", e.target.value)}
                                    sx={{
                                        "& .MuiInputBase-input": {
                                            color: "#9D0385",
                                            fontWeight: "bold",
                                        },
                                    }}
                                >
                                    {intakePeriods.map((opt) => (
                                        <MenuItem key={opt.id} value={opt.id}>
                                            {opt.intake} ({opt.description})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Intake Year */}
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth>
                                <InputLabel>Year</InputLabel>
                                <Select
                                    value={formData.intake_year || ""}
                                    onChange={(e) => handleChange("intake_year", e.target.value)}
                                    sx={{
                                        "& .MuiInputBase-input": {
                                            color: "#9D0385",
                                            fontWeight: "bold",
                                        },
                                    }}
                                >
                                    {intakeYears.map((year) => (
                                        <MenuItem key={year} value={year}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Target Country */}
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth>
                                <InputLabel>Target Country</InputLabel>
                                <Select
                                    value={formData.target_country_id || ""}
                                    onChange={(e) => handleChange("target_country_id", e.target.value)}
                                    sx={{
                                        "& .MuiInputBase-input": {
                                            color: "#9D0385",
                                            fontWeight: "bold",
                                        },
                                    }}
                                >
                                    {countryOptions.map((opt) => (
                                        <MenuItem key={opt.id} value={opt.id}>
                                            {opt.country_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions className="p-6 pt-0">
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        className="bg-[#572575] hover:bg-[#572575] text-white px-8 py-2 rounded-lg flex items-center gap-2 w-[50%] justify-center mx-auto"
                    >
                        <Check /> Yes, Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    )
}

export default PrefrenceModal
